import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, nanoid, PayloadAction } from "@reduxjs/toolkit";

import { reportsModelingService } from "../../services/reports-modeling-service";
import { REPORT_LEVELS, REPORT_TYPES, REPORT_VARIANT_TYPES } from "../../utils/sodReports.js";
import { BUSY_TYPES } from "../../utils/busy.js";
import {
    REPORT_MODELING_CONFLICT_LEVELS,
    getDeletedDefaultValuesForTcodes,
    recalcRoleTreeTrafficLightStatus,
    REPORT_MODELING_DELTA_STATUSES,
} from "../../utils/reports-modeling-utils";
import { getSelectedRoleReducers } from "./selectedRoleReducers";
import {
    getDefaultPermissionsValuesForTransactions,
    getTransactionFromRoleMenu,
    getPfcgTreeForTcodes,
    getDeletedTcodesFromMenu,
    getTcodesMapCountFromMenu,
    getTransactionsByIds,
    getDefaultPermissionsValuesForTransactionsMap,
} from "../../utils/role-menu";
import { findItemInTree, TreeWalker, findFieldInTree } from "../../utils/tree-table.js";
import produce from "immer";
import { removeGroupRoleChildren } from "../../utils/group-role-utils.js";
import {
    FieldItem,
    ModelingRole,
    RoleChildren,
    RoleForView,
    RoleModelingState,
    RoleVariantDetailed,
    SaveVariantDataType,
    RoleVariant,
} from "../../types/role-modeling/role-modeling-types.js";
import { AppDispatch, RootState } from "../../store.js";
import { ReportColumn } from "../../types/report/report-types";
import { FetchTableDataParams } from "../../types/common/table/table-types";
import { ResponseError } from "types/common/responses/responses-types";

const modelingRolesAdapter = createEntityAdapter<ModelingRole>();

const initialReportData = {
    canFetchResults: false,
    resultsOpen: false,
    reportLevel: REPORT_LEVELS.OPERATIONS,
    reportType: REPORT_TYPES.FINAL,
    conflictLevel: REPORT_MODELING_CONFLICT_LEVELS.ROLE,
    rows: [],
    columns: [],
    total: 0,
    selectedIds: [],
    searchString: "",
    pageNumber: 1,
    limitNumber: 10,
    sortData: [],
    filterData: [],
    calculatedConflictLevels: [],
};

const initialState: RoleModelingState["reportsModeling"]["roleLevel"] = {
    parameters: {
        roles: [],
        systems: [],
        matrixHeaders: [],
        risks: [],
        riskLevels: [],
    },

    standardParameters: {
        roles: [],
        systems: [],
        matrixHeaders: [],
        risks: [],
        riskLevels: [],
    },

    parametersOpen: true,

    report: {
        ...initialReportData,
    },

    busy: false,
    busyType: null,
    error: null,
    validationErrors: null,

    variantCreated: false,
    variants: [],
    currentVariant: null,

    modeling: {
        open: false,
        roles: modelingRolesAdapter.getInitialState(),
        selectedRoleId: "",
        rolesForView: [],
        selectedViewRole: null,
    },
};

const createRoleForModeling = (role: RoleForView, initialized: boolean = false): ModelingRole => ({
    id: role.id,
    role: role.role,
    description: role.description,
    isComplex: role.isComplex,
    systemId: role.systemId,
    initialized: initialized ? initialized : role.isComplex,
    menu: reportsModelingService.parseRoleMenu([]),
    tree: [],
    orgLevels: {},
    children: role.children,
    delta: [],
    orgLevelsDelta: [],
    profile: "",
    status: role.status,
    changed: false,
    added: false,
});

const createRoleForView = (detailedRole: ModelingRole): RoleForView => ({
    id: detailedRole.id,
    role: detailedRole.role,
    systemId: detailedRole.systemId,
    description: detailedRole.description,
    isComplex: detailedRole.isComplex,
    children: detailedRole.children,
    status: null,
    open: false,
});

const createRoleForViewByChildren = (childRole: RoleChildren): RoleForView => ({
    id: childRole.id,
    role: childRole.role,
    systemId: childRole.systemId,
    description: childRole.description,
    isComplex: false,
    children: [],
    status: childRole.status,
    open: false,
});

export const removeMenuItemFromTree = createAsyncThunk<void, { menuItemPath: string[]; id: string }, { state: RootState }>(
    "reportsModeling/roleLevel/removeMenuItem",
    async ({ menuItemPath, id }, { getState, dispatch }) => {
        const state = getState();

        const selectedRole = selectModelingRoleById(state, id);

        if (!selectedRole) {
            return;
        }

        const removedMenuItem = findItemInTree(menuItemPath, selectedRole.menu);

        const tcodesMapItem = getTcodesMapCountFromMenu([removedMenuItem]);

        dispatch(
            removeRoleMenuNode({
                id: selectedRole.id,
                activeNodePath: menuItemPath,
            })
        );

        const tcodesMapMenu = getTcodesMapCountFromMenu(selectedRole.menu);

        const tcodeToValue = await getDefaultPermissionsValuesForTransactionsMap(selectedRole.systemId, Object.keys(tcodesMapMenu));

        const { deletedTcodes, remainedTcodes, deletedAtBranchTcodes } = getDeletedTcodesFromMenu(tcodesMapItem, tcodesMapMenu);

        try {
            const { orgLevelsForDelete, profilesForDelete } = getDeletedDefaultValuesForTcodes(
                deletedTcodes,
                remainedTcodes,
                tcodeToValue,
                deletedAtBranchTcodes
            );

            dispatch(
                substractPfcgTreeFromRole({
                    id,
                    orgLevelsForDelete,
                    profilesForDelete,
                    removedTcodes: Array.from(deletedTcodes),
                })
            );
        } catch (error) {
            console.log(error);
        }
    }
);

export const fetchReportColumns = createAsyncThunk<ReportColumn[], void, { state: RootState; rejectValue: ResponseError }>(
    "reportsModeling/roleLevel/fetchReportColumns",
    async (_, { getState, rejectWithValue }) => {
        const state = getState();
        const conflictLevel = state.reportsModeling.roleLevel.report.conflictLevel;
        try {
            const columns =
                conflictLevel === REPORT_MODELING_CONFLICT_LEVELS.USERNAME
                    ? await reportsModelingService.getUserLevelModelingColumns(
                          state.reportsModeling.roleLevel.report.reportLevel,
                          state.reportsModeling.roleLevel.report.reportType
                      )
                    : await reportsModelingService.getRoleLevelModelingColumns(
                          state.reportsModeling.roleLevel.report.reportLevel,
                          state.reportsModeling.roleLevel.report.reportType
                      );
            return columns;
        } catch (error) {
            rejectWithValue(error as ResponseError);
        }
    }
);

export const fetchReportResults = createAsyncThunk<
    { rows: []; total: number },
    { params: FetchTableDataParams },
    { state: RootState; rejectValue: ResponseError }
>("reportsModeling/roleLevel/fetchReportResults", async ({ params }, { getState, rejectWithValue }) => {
    const state = getState();

    if (!state.reportsModeling.roleLevel.report.canFetchResults) {
        throw new Error("Can't fetch results");
    }

    const conflictLevel = state.reportsModeling.roleLevel.report.conflictLevel;

    const newParams = {
        ...params,
        report_level: state.reportsModeling.roleLevel.report.reportLevel,
        report_type: state.reportsModeling.roleLevel.report.reportType,
        conflict_level: conflictLevel,
    };
    try {
        const response =
            conflictLevel === REPORT_MODELING_CONFLICT_LEVELS.USERNAME
                ? await reportsModelingService.getUserLevelReportResults(newParams)
                : await reportsModelingService.getRoleLevelReportResults(newParams);

        return response as { rows: []; total: number };
    } catch (error) {
        return rejectWithValue(error as ResponseError);
    }
});

export const calcReport = createAsyncThunk<
    { conflictLevel: string; calcWithReset: boolean } | void | ResponseError,
    { conflictLevel: string; calcWithReset: boolean },
    { state: RootState; rejectValue: ResponseError }
>("reportsModeling/roleLevel//calcReport", async ({ conflictLevel, calcWithReset }, { getState, rejectWithValue }) => {
    const state = getState();

    const alreadyCalculated = state.reportsModeling.roleLevel.report.calculatedConflictLevels.includes(conflictLevel);

    if (alreadyCalculated && !calcWithReset) {
        return;
    }

    const currentVariant = state.reportsModeling.roleLevel.currentVariant;
    const groupRoles = state.reportsModeling.roleLevel.modeling.rolesForView.filter((role) => role.isComplex);

    const modelingRoles = Object.values(state.reportsModeling.roleLevel.modeling.roles.entities).filter((role) => !!role);
    const rolesForView = state.reportsModeling.roleLevel.modeling.rolesForView;

    const changedModelingRoles = modelingRoles.filter((role) => role?.changed && !role?.isComplex);

    const parsedModelingRoles = reportsModelingService.parseModelingRolesVariantForServer(changedModelingRoles, rolesForView);
    const parsedGroupRoles = reportsModelingService.parseModelingGroupRolesForServer(groupRoles);

    const systems = state.reportsModeling.roleLevel.parameters.systems.map((item) => item.key);

    const form = {
        roles: state.reportsModeling.roleLevel.parameters.roles,
        systems: systems,
        matrix_headers: state.reportsModeling.roleLevel.parameters.matrixHeaders.map((item) => item.key),
        risks: state.reportsModeling.roleLevel.parameters.risks.map((item) => item.key),
        risk_levels: state.reportsModeling.roleLevel.parameters.riskLevels.map((item) => item.key),
        groups: [],
        changed_roles: parsedModelingRoles,
        changed_group_roles: parsedGroupRoles,
        variant_id: currentVariant ? currentVariant.id : null,
    };

    if (conflictLevel === REPORT_MODELING_CONFLICT_LEVELS.USERNAME) {
        try {
            await reportsModelingService.calcUserLevelModelingReport(form, conflictLevel);
            return { conflictLevel: conflictLevel, calcWithReset: calcWithReset };
        } catch (error) {
            return rejectWithValue(error as ResponseError);
        }
    }

    try {
        await reportsModelingService.calcRoleLevelModelingReport(form, conflictLevel);
        return { conflictLevel: conflictLevel, calcWithReset: calcWithReset };
    } catch (error) {
        return rejectWithValue(error as ResponseError);
    }
});

export const calcCompareReport = createAsyncThunk<void, void, { state: RootState; rejectValue: ResponseError }>(
    "reportsModeling/roleLevel//calcCompareReport",
    async (_, { getState, rejectWithValue }) => {
        const state = getState();

        const currentVariant = state.reportsModeling.roleLevel.currentVariant;

        const groupRoles = state.reportsModeling.roleLevel.modeling.rolesForView.filter((role) => role.isComplex);

        const modelingRoles = Object.values(state.reportsModeling.roleLevel.modeling.roles.entities).filter((role) => !!role);
        const rolesForView = state.reportsModeling.roleLevel.modeling.rolesForView;

        const changedModelingRoles = modelingRoles.filter((role) => role?.changed && !role?.isComplex);

        const parsedModelingRoles = reportsModelingService.parseModelingRolesVariantForServer(changedModelingRoles, rolesForView);
        const parsedGroupRoles = reportsModelingService.parseModelingGroupRolesForServer(groupRoles);

        const systems = state.reportsModeling.roleLevel.parameters.systems.map((item) => item.key);

        const form = {
            roles: state.reportsModeling.roleLevel.parameters.roles,
            systems: systems,
            matrix_headers: state.reportsModeling.roleLevel.parameters.matrixHeaders.map((item) => item.key),
            risks: state.reportsModeling.roleLevel.parameters.risks.map((item) => item.key),
            risk_levels: state.reportsModeling.roleLevel.parameters.riskLevels.map((item) => item.key),
            groups: [],
            changed_roles: parsedModelingRoles,
            changed_group_roles: parsedGroupRoles,
            variant_id: currentVariant ? currentVariant.id : null,
        };

        try {
            await reportsModelingService.calcRoleLevelModelingCompareReport(form);
        } catch (error) {
            return rejectWithValue(error as ResponseError);
        }
    }
);

export const saveVariant = createAsyncThunk<RoleVariantDetailed, { variantName: string }, { state: RootState; rejectValue: ResponseError }>(
    "reportsModeling/roleLevel/saveVariant",
    async ({ variantName }, { getState, rejectWithValue, dispatch }) => {
        const state = getState();

        const variant = state.reportsModeling.roleLevel.variants.find((variant) => variant.name === variantName);

        const currentVariant = state.reportsModeling.roleLevel.currentVariant;

        const isVariantNew = !variant;

        const sourceVariantId = currentVariant && currentVariant.id === variant?.id ? null : currentVariant?.id;

        const modelingRoles = Object.values(state.reportsModeling.roleLevel.modeling.roles.entities).filter((role) => !!role);

        const rolesForView = state.reportsModeling.roleLevel.modeling.rolesForView;

        const requestData: SaveVariantDataType = {
            id: variant ? variant.id : null,
            variant_name: variantName,
            variant_type: REPORT_VARIANT_TYPES.ROLES_MODELING,
            source_variant_id: sourceVariantId || null,
            variant_data: {
                roles: state.reportsModeling.roleLevel.parameters.roles,
                systems: state.reportsModeling.roleLevel.parameters.systems.map((item) => item.key),
                matrix_headers: state.reportsModeling.roleLevel.parameters.matrixHeaders.map((item) => item.key),
                risks: state.reportsModeling.roleLevel.parameters.risks.map((item) => item.key),
                risk_levels: state.reportsModeling.roleLevel.parameters.riskLevels.map((item) => item.key),
            },
            roles: reportsModelingService.parseModelingRolesVariantForServer(modelingRoles, rolesForView),
        };

        let savedVariant: RoleVariantDetailed | null = null;

        try {
            savedVariant = await reportsModelingService.saveRoleVariant(requestData, isVariantNew);
        } catch (error) {
            return rejectWithValue(error as ResponseError);
        }

        dispatch(initModelingByVariant({ variant: savedVariant }));

        return savedVariant;
    }
);

export const deleteVariant = createAsyncThunk<RoleVariant, string, { rejectValue: ResponseError; state: RootState }>(
    "reportsModeling/roleLevel/deleteVariant",
    async (variantName, { rejectWithValue, getState }) => {
        const state = getState();
        const variant = state.reportsModeling.roleLevel.variants.find((variant) => variant.name === variantName);
        if (!variant) throw Error("Variant not found");

        try {
            await reportsModelingService.deleteVariant(variant?.id);
        } catch (error) {
            return rejectWithValue(error as ResponseError);
        }

        return variant;
    }
);

export const setFavoritesForVariant = createAsyncThunk<
    { variantName: string; inFavorites: boolean },
    { variant: RoleVariant; inFavorites: boolean },
    { rejectValue: ResponseError }
>("reportsModeling/roleLevel/setFavoritesForVariant", async ({ variant, inFavorites }, { rejectWithValue }) => {
    const form = {
        variant_name: variant.name,
        variant_type: REPORT_VARIANT_TYPES.ROLES_MODELING,
        in_favorites: inFavorites,
        variant_id: variant.id,
    };

    try {
        await reportsModelingService.setFavoritesForVariant(form);

        return { variantName: variant.name, inFavorites: inFavorites };
    } catch (error) {
        return rejectWithValue(error as ResponseError);
    }
});

export const fetchVariantsByUser = createAsyncThunk<RoleVariant[], string, { rejectValue: ResponseError }>(
    "reportsModeling/roleLevel/fetchVariantsByUser",
    async (searchString, { rejectWithValue }) => {
        const params = {
            variant_type: REPORT_VARIANT_TYPES.ROLES_MODELING,
        };

        if (searchString) {
            params["search"] = searchString;
        }
        try {
            const response = await reportsModelingService.getVariantsByUser(params);

            return response as RoleVariant[];
        } catch (error) {
            return rejectWithValue(error as ResponseError);
        }
    }
);

export const exportReport = createAsyncThunk<void, void, { state: RootState; rejectValue: ResponseError }>(
    "reportsModeling/roleLevel/exportReport",
    async (_, { getState, rejectWithValue }) => {
        const state = getState();
        const conflictLevel = state.reportsModeling.roleLevel.report.conflictLevel;

        const params = {
            report_level: state.reportsModeling.roleLevel.report.reportLevel,
            report_type: state.reportsModeling.roleLevel.report.reportType,
            conflict_level: conflictLevel,
        };

        try {
            try {
                const exportMap = new Map([
                    [REPORT_MODELING_CONFLICT_LEVELS.USERNAME, reportsModelingService.exportUserLevelReport],
                    [REPORT_MODELING_CONFLICT_LEVELS.ROLE, reportsModelingService.exportRoleLevelReport],
                ]);

                const exportReport = exportMap.get(conflictLevel) ?? reportsModelingService.exportRoleLevelReport;

                await exportReport(params);
            } catch (error) {
                return rejectWithValue(error as ResponseError);
            }
        } catch (error) {
            rejectWithValue(error as ResponseError);
        }
    }
);

export const setReportLevelWithColumns = (reportLevel) => (dispatch) => {
    dispatch(setReportLevel(reportLevel));
    dispatch(fetchReportColumns());
};

export const setReportTypeWithColumns = (reportType) => (dispatch) => {
    dispatch(setReportType(reportType));
    dispatch(fetchReportColumns());
};

export const setConflictLevelWithColumns = (conflictLevel) => (dispatch) => {
    dispatch(setConflictLevel(conflictLevel));
    dispatch(fetchReportColumns());
};

export const saveReportColumns = createAsyncThunk<
    { columns: ReportColumn[]; reportLevel: string; reportType: string; conflictLevel: string },
    { columns: ReportColumn[]; reportLevel: string; reportType: string; conflictLevel: string }
>("reportsModeling/roleLevel/saveReportColumns", async ({ columns, reportLevel, reportType, conflictLevel }) => {
    if (conflictLevel === REPORT_MODELING_CONFLICT_LEVELS.USERNAME) {
        await reportsModelingService.saveUserLevelModelingColumns(columns, reportLevel, reportType);
    } else {
        await reportsModelingService.saveRoleLevelModelingColumns(columns, reportLevel, reportType);
    }
    return { columns, reportLevel, reportType, conflictLevel };
});

export const setSelectedViewRole = createAsyncThunk<RoleForView, RoleForView, { dispatch: AppDispatch }>(
    "reportsModeling/userLevel/setSelectedModelingRole",
    async (role, { dispatch }) => {
        dispatch(setModelingSelectedRoleById(role?.id));
        return role;
    }
);

export const setModelingSelectedRole = createAsyncThunk<
    ModelingRole | null,
    { role: string | null },
    { state: RootState; rejectValue: ResponseError }
>("reportsModeling/roleLevel/setModelingSelectedRole", async ({ role }, { rejectWithValue, getState }) => {
    if (!role) return null;

    const modelingRoles = selectModelingRoles(getState());

    const currentModelingRole = modelingRoles.find((item) => item.role === role);

    if (currentModelingRole && currentModelingRole.initialized) {
        return currentModelingRole;
    }

    const state = getState();

    const currentVariant = state.reportsModeling.roleLevel.currentVariant;

    if (!currentModelingRole) {
        throw Error("Current role is not defined");
    }

    let roleDetailed: ModelingRole | null = null;

    try {
        if (currentVariant && !currentModelingRole.added) {
            roleDetailed = await reportsModelingService.getDetailedVariantRole(
                currentVariant.id,
                currentModelingRole.role,
                currentModelingRole.systemId
            );
        } else {
            roleDetailed = await reportsModelingService.getDetailedIntegrationRole(currentModelingRole.role, currentModelingRole.systemId);
        }

        if (!roleDetailed) {
            throw Error("Role is not defined");
        }

        roleDetailed.id = currentModelingRole.id;
        roleDetailed.added = currentModelingRole.added;

        recalcRoleTreeTrafficLightStatus(roleDetailed.tree, roleDetailed.orgLevels);

        return roleDetailed;
    } catch (error) {
        rejectWithValue(error as ResponseError);
    }

    return null;
});

export const findPermissionInTree = createAsyncThunk<void, { searchString: string }, { state: RootState }>(
    "reportsModeling/roleLevel/findPermissionInTree",
    async ({ searchString }, { getState, dispatch }) => {
        const state = getState();
        const roleId = state.reportsModeling.roleLevel.modeling.selectedRoleId;
        const role = state.reportsModeling.roleLevel.modeling.roles.entities[roleId];

        if (role && role.tree && role.tree.length) {
            const updatedTree = produce(role.tree, (tree) => {
                findFieldInTree(tree, searchString);
            });

            dispatch(
                updateModelingRole({
                    id: roleId,
                    changes: { tree: updatedTree },
                })
            );
        }
    }
);

export const setModelingSelectedRoleById = (id) => (dispatch, getState) => {
    const state = getState();
    const role = selectModelingRoleById(state, id);
    dispatch(setModelingSelectedRole({ role: role?.role || null }));
};

export const setModelingSelectedRoleByName =
    (name = "") =>
    (dispatch, getState) => {
        const state = getState();

        const lowerName = name.toLowerCase();

        const role = selectModelingRoles(state).find((role) => role.role.toLowerCase() === lowerName);

        dispatch(setModelingSelectedRole({ role: role?.role || null }));
    };

export const addDefaultPermissionsForMenuUpdate = createAsyncThunk<
    void,
    { id: string; transactionIds: string[] },
    { state: RootState; dispatch: AppDispatch }
>("reportsModeling/roleLevel/addDefaultPermissionsForMenuUpdate", async ({ id, transactionIds }, { getState, dispatch }) => {
    const state = getState();
    const role = selectModelingRoleById(state, id);

    if (!role || !transactionIds?.length) {
        return;
    }

    dispatch(setBusy(true));

    await Promise.all(
        transactionIds.map(async (tcode) => {
            const newPermissionsTree = await getPfcgTreeForTcodes(role.systemId, [tcode]);

            const defaultValues = await getDefaultPermissionsValuesForTransactions(role.systemId, [tcode], null);

            dispatch(
                addModelingRoleTreeItems({
                    id,
                    items: newPermissionsTree,
                    defaultValues,
                    tcodes: transactionIds,
                    isStandard: true,
                })
            );
        })
    );

    dispatch(setBusy(false));
});

export const addRoleMenuNodeWithDefaultValues =
    ({ id, activeNodePath, tcodes }) =>
    async (dispatch, getState) => {
        let state = getState();
        let selectedRole = selectModelingRoleById(state, id);

        if (!selectedRole) {
            return;
        }

        const transactions = await getTransactionsByIds(selectedRole.systemId, tcodes);

        dispatch(
            addRoleMenuNode({
                id,
                activeNodePath,
                tcodes,
                transactions,
            })
        );

        state = getState();
        selectedRole = selectModelingRoleById(state, id);
        const tcodesMapMenu = getTcodesMapCountFromMenu(selectedRole?.menu);

        const tcodesForDefaultPermissions = tcodes.filter((tcode) => tcodesMapMenu[tcode] && tcodesMapMenu[tcode] > 0);

        if (tcodesForDefaultPermissions.length > 0) {
            dispatch(
                addDefaultPermissionsForMenuUpdate({
                    id,
                    transactionIds: tcodesForDefaultPermissions,
                })
            );
        }
    };

export const addGroupRoleChildToModeling = createAsyncThunk<void, { childRole: RoleChildren; parentId: string }, { state: RootState }>(
    "reportsModeling/userLevel/addGroupRoleChildToModeling",
    async ({ parentId, childRole }, { getState, dispatch }) => {
        const state = getState();
        const roles = state.reportsModeling.roleLevel.modeling.rolesForView;
        const currentModelingRoles = Object.values(state.reportsModeling.roleLevel.modeling.roles.entities).filter((role) => !!role);

        const existingRole = currentModelingRoles.find((role) => role.role === childRole.role && role.systemId === childRole.systemId);

        if (existingRole) {
            return;
        }

        childRole.id = nanoid();

        const roleForView = createRoleForViewByChildren(childRole);

        const modelingChildRole = createRoleForModeling(roleForView, false);

        modelingChildRole.added = true;

        dispatch(addModelingRole(modelingChildRole));

        const updatedRoles = roles.map((role) => {
            if (role.id !== parentId) {
                return role;
            }

            const child = role.children.find((item) => item.role == childRole.role);

            if (child) return role;

            const childRoleCopy = { ...childRole, parentRole: role.role };

            return {
                ...role,
                status: REPORT_MODELING_DELTA_STATUSES.CHANGED,
                children: [...role.children, childRoleCopy],
            };
        });

        dispatch(setRolesForView(updatedRoles));
    }
);

export const removeGroupRoleChildFromModeling = createAsyncThunk<void, { roleIdToRemove: string }, { state: RootState }>(
    "reportsModeling/userLevel/removeGroupRoleChildFromModeling",
    async ({ roleIdToRemove }, { getState, dispatch }) => {
        const state = getState();
        const roles = state.reportsModeling.roleLevel.modeling.rolesForView;

        const updatedRoles = roles
            .map((role) => {
                if (role.isComplex) {
                    return {
                        ...role,
                        status: REPORT_MODELING_DELTA_STATUSES.CHANGED,
                        children: removeGroupRoleChildren(roleIdToRemove, role.children),
                    };
                }
                return role;
            })
            .filter((role) => role.id !== roleIdToRemove);

        dispatch(setRolesForView(updatedRoles));

        dispatch(removeRoleFromModeling(roleIdToRemove));
    }
);

export const copyRoleMenuWithDefaultValues =
    ({ id, menuToCopy }) =>
    (dispatch) => {
        if (!menuToCopy?.length) {
            return;
        }

        dispatch(
            copyRoleMenu({
                id,
                menuToCopy,
            })
        );

        const transactionIds = getTransactionFromRoleMenu(menuToCopy[0].children);

        dispatch(
            addDefaultPermissionsForMenuUpdate({
                id,
                transactionIds,
            })
        );
    };

export const initModelingByVariant = createAsyncThunk<
    { variant: RoleVariantDetailed },
    { variant: RoleVariantDetailed },
    { rejectValue: ResponseError }
>("reportsModeling/roleLevel/initModelingByVariant", async ({ variant }, { dispatch, rejectWithValue }) => {
    try {
        const detailedVariant = await reportsModelingService.getDetailedRoleVariant(variant);

        dispatch(setCurrentVariantFields(detailedVariant));
        dispatch(setOpenModeling());
        dispatch(clearReport());
        // dispatch(clearVariant());

        if (!detailedVariant) {
            throw Error("Varian is not defined");
        }

        return { variant: detailedVariant };
    } catch (error) {
        return rejectWithValue(error as ResponseError);
    }
});

export const initModelingManual = createAsyncThunk<
    { roleForView: RoleForView; modelingRole: ModelingRole },
    null,
    { state: RootState; rejectValue: ResponseError }
>("reportsModeling/roleLevel/initModelingManual", async (_, { getState, rejectWithValue, dispatch }) => {
    const state = getState();

    dispatch(setOpenModeling());
    dispatch(clearReport());
    dispatch(clearVariant());

    const selectedRoles = state.reportsModeling.roleLevel.parameters.roles;
    const selectedSystems = state.reportsModeling.roleLevel.parameters.systems;

    const roleName = selectedRoles && selectedRoles[0];
    const systemId = selectedSystems && selectedSystems[0]?.key;

    if (!roleName || !systemId) {
        throw Error("System is not defined");
    }

    try {
        const detailedRole = await reportsModelingService.getDetailedIntegrationRole(roleName, systemId);
        if (!detailedRole) {
            throw Error("Detailed role is not defined");
        }

        const roleForView = createRoleForView(detailedRole);

        return {
            roleForView: roleForView,
            modelingRole: detailedRole,
        };
    } catch (error) {
        return rejectWithValue(error as ResponseError);
    }
});

// export const setVariantWithFetchModeling = createAsyncThunk<void, RoleVariantDetailed | null>(
//     "reportsModeling/roleLevel/setVariantWithFetchModeling",
//     async (variant, { dispatch }) => {
//         // dispatch(setCurrentVariantFields(variant));

//         // if (!variant) {
//         //     dispatch(clearVariant());
//         //     return;
//         // }
//     }
// );

const selectedRoleReducers = getSelectedRoleReducers(
    (state) => state.modeling.roles,
    modelingRolesAdapter,
    (state, role) => {
        const rolesForView = state.modeling.rolesForView;

        const changedRole = rolesForView.find((item) => item.id === role.id);

        if (changedRole) {
            changedRole.status = REPORT_MODELING_DELTA_STATUSES.CHANGED;
        }

        rolesForView.forEach((r) => {
            if (r.isComplex) {
                r.children.forEach((child) => {
                    if (child.id == role.id) {
                        child.status = REPORT_MODELING_DELTA_STATUSES.CHANGED;
                    }
                });
            }
        });

        role.changed = true;
    },
    false
);

const roleLevelSlice = createSlice({
    name: "reportsModeling/roleLevel",
    initialState: initialState,
    reducers: {
        setBusy(state, action) {
            state.busy = action.payload;
        },

        setRoles(state, action: PayloadAction<string[]>) {
            state.parameters.roles = action.payload;
        },

        setSystems(state, action: PayloadAction<FieldItem[]>) {
            state.parameters.systems = action.payload;
        },

        setMatrixHeaders(state, action) {
            state.parameters.matrixHeaders = action.payload;
        },

        setRisks(state, action) {
            state.parameters.risks = action.payload;
        },

        setRiskLevels(state, action) {
            state.parameters.riskLevels = action.payload;
        },

        setParametersOpen(state, action) {
            state.parametersOpen = action.payload;
        },

        setResultsOpen(state, action) {
            state.report.resultsOpen = action.payload;
        },

        setReportLevel(state, action) {
            state.report.pageNumber = 1;
            state.report.searchString = "";
            state.report.filterData = [];
            state.report.sortData = [];
            state.report.reportLevel = action.payload;
        },

        setReportType(state, action) {
            state.report.pageNumber = 1;
            state.report.searchString = "";
            state.report.filterData = [];
            state.report.sortData = [];
            state.report.reportType = action.payload;
        },

        setConflictLevel(state, action) {
            state.report.pageNumber = 1;
            state.report.searchString = "";
            state.report.filterData = [];
            state.report.sortData = [];
            state.report.conflictLevel = action.payload;
        },

        setError(state, action) {
            state.error = action.payload;
        },

        setSelectedIds(state, action) {
            state.report.selectedIds = action.payload;
        },

        setSearchString(state, action) {
            if (state.report.searchString !== action.payload) {
                state.report.pageNumber = 1;
                state.report.searchString = action.payload;
            }
        },

        setPageNumber(state, action) {
            state.report.pageNumber = action.payload;
        },

        setLimitNumber(state, action) {
            state.report.pageNumber = 1;
            state.report.limitNumber = action.payload;
        },

        setSortData(state, action) {
            state.report.pageNumber = 1;
            state.report.sortData = action.payload;
        },

        setFilterData(state, action) {
            state.report.pageNumber = 1;
            state.report.filterData = action.payload;
        },

        setOpenModeling(state) {
            state.busy = false;
            state.parametersOpen = false;
            state.modeling.open = true;
        },

        setCurrentVariantFields(state, action) {
            state.standardParameters.roles = state.parameters.roles;
            state.standardParameters.systems = state.parameters.systems;
            state.standardParameters.matrixHeaders = state.parameters.matrixHeaders;
            state.standardParameters.risks = state.parameters.risks;
            state.standardParameters.riskLevels = state.parameters.riskLevels;

            const variant = action.payload;

            state.currentVariant = variant;

            state.parameters.roles = variant ? variant.data.roles : state.standardParameters.roles;

            state.parameters.systems = variant ? variant.data.systems : state.standardParameters.systems;

            state.parameters.matrixHeaders = variant ? variant.data.matrixHeaders : state.standardParameters.matrixHeaders;

            state.parameters.risks = variant ? variant.data.risks : state.standardParameters.risks;

            state.parameters.riskLevels = variant ? variant.data.riskLevels : state.standardParameters.riskLevels;
        },

        setModelingOpen(state, action) {
            state.modeling.open = action.payload;
        },

        setRolesForView(state, action) {
            state.modeling.rolesForView = action.payload;
        },

        removeRoleFromModeling: (state, action) => {
            modelingRolesAdapter.removeOne(state.modeling.roles, action.payload);
        },

        addModelingRole(state, action) {
            modelingRolesAdapter.addOne(state.modeling.roles, action.payload);
        },

        updateModelingRole: (state, action) => {
            modelingRolesAdapter.updateOne(state.modeling.roles, action.payload);
        },

        clearFilters(state) {
            state.report.selectedIds = [];
            state.report.searchString = "";
            state.report.pageNumber = 1;
            state.report.sortData = [];
            state.report.filterData = [];
        },

        clearVariant(state) {
            state.modeling.roles = modelingRolesAdapter.getInitialState();
            state.modeling.rolesForView = [];
            state.modeling.selectedRoleId = "";
            state.currentVariant = null;
        },
        clearParameters(state) {
            state.parameters.matrixHeaders = [];
            state.parameters.riskLevels = [];
            state.parameters.risks = [];
            state.parameters.roles = [];
            state.parameters.systems = [];
            state.parametersOpen = true;
            state.modeling.open = false;
        },

        clearReport(state) {
            for (const resultsKey in initialReportData) {
                state.report[resultsKey] = initialReportData[resultsKey];
            }
        },

        expandGroupRole(state, action) {
            const rolesForView = state.modeling.rolesForView;
            const stayOpen = action.payload.stayOpen;
            const groupRole = rolesForView.find((role) => role.id === action.payload.role.id);

            if (groupRole) {
                groupRole.open = !groupRole?.open;

                if (stayOpen) {
                    groupRole.open = true;
                }
            }
        },

        expandItemTree(state, action) {
            const roleId = state.modeling.selectedRoleId;
            const role = state.modeling.roles.entities[roleId];

            if (!role) {
                return;
            }

            const item = findItemInTree(action.payload.path, role.tree);
            item.open = !item.open;
            const treeWalker = new TreeWalker(item.children);
            treeWalker.walk(
                (node) => {
                    node.open = item.open;
                },
                () => {
                    return {
                        keyField: "",
                        keyValue: "",
                        childField: "children",
                    };
                },
                () => {}
            );
        },

        ...selectedRoleReducers,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchReportResults.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
                state.report.rows = [];
            })
            .addCase(fetchReportResults.rejected, (state, action) => {
                const error = action.payload;
                state.busy = false;
                if (!error) return;
                state.error = error;

                if (error && error.code === "validation") {
                    state.validationErrors = error.errors;
                }
            })
            .addCase(fetchReportResults.fulfilled, (state, action) => {
                state.busy = false;
                state.error = null;
                state.validationErrors = null;

                if (action.payload) {
                    const { rows, total } = action.payload as { rows: []; total: number };
                    state.report.resultsOpen = true;
                    state.report.rows = rows;
                    state.report.total = total;
                }
            })
            .addCase(calcReport.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
                state.error = null;
                state.validationErrors = null;
            })
            .addCase(calcReport.rejected, (state, action) => {
                state.busy = false;
                const error = action.payload;
                if (!error) return;

                state.busy = false;
                state.error = error;

                if (error && error.code === "validation") {
                    state.validationErrors = error.errors;
                }
            })
            .addCase(calcReport.fulfilled, (state, action) => {
                state.report.selectedIds = [];
                state.report.searchString = "";
                state.report.pageNumber = 1;
                state.report.sortData = [];
                state.report.filterData = [];
                state.report.canFetchResults = true;
                if (!action.meta.arg.calcWithReset) {
                    state.report.conflictLevel = action.meta.arg.conflictLevel;
                    const conflictLevel = action.meta.arg.conflictLevel;
                    if (!state.report.calculatedConflictLevels.includes(conflictLevel)) {
                        state.report.calculatedConflictLevels.push(conflictLevel);
                    }
                } else {
                    state.report.conflictLevel = REPORT_MODELING_CONFLICT_LEVELS.ROLE;
                    state.report.calculatedConflictLevels = ["role"];
                }
            })
            .addCase(calcCompareReport.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
                state.error = null;
                state.validationErrors = null;
            })
            .addCase(calcCompareReport.rejected, (state, action) => {
                state.busy = false;
                const error = action.payload;
                if (!error) return;

                state.busy = false;
                state.error = error;

                if (error && error.code === "validation") {
                    state.validationErrors = error.errors;
                }
            })
            .addCase(calcCompareReport.fulfilled, (state) => {
                state.report.selectedIds = [];
                state.report.searchString = "";
                state.report.pageNumber = 1;
                state.report.sortData = [];
                state.report.filterData = [];
                state.report.canFetchResults = true;
            })
            .addCase(fetchReportColumns.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })
            .addCase(fetchReportColumns.fulfilled, (state, action) => {
                state.busy = false;
                state.report.columns = action.payload?.filter((column) => column.active);
            })
            .addCase(fetchReportColumns.rejected, (state, action) => {
                state.busy = false;
                if (!action.payload) return;
                state.error = action.payload;
            })
            .addCase(saveVariant.pending, (state) => {
                state.variantCreated = false;
                state.busy = true;
                state.busyType = BUSY_TYPES.SAVE;
            })
            .addCase(saveVariant.fulfilled, (state, action) => {
                state.busy = false;
                state.validationErrors = [];
                state.variantCreated = true;

                const savedVariant = action.payload;
                const index = state.variants.findIndex((variant) => variant.name === savedVariant.name);

                if (index === -1) {
                    state.variants.push(savedVariant);
                } else {
                    state.variants[index] = savedVariant;
                }
            })
            .addCase(saveVariant.rejected, (state, action) => {
                const error = action.payload;
                state.busy = false;
                if (!error) return;
                state.error = error;

                if (error && error.code === "validation") {
                    state.validationErrors = error.errors;
                }
            })
            .addCase(deleteVariant.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.SAVE;
            })
            .addCase(deleteVariant.fulfilled, (state, action) => {
                const deletedVariant = action.payload;

                state.busy = false;
                state.variants = state.variants.filter((variant) => variant.id !== deletedVariant.id);

                if (state.currentVariant?.id === deletedVariant.id) {
                    state.currentVariant = null;
                    state.parameters.roles = [];
                    state.parameters.systems = [];
                    state.parameters.matrixHeaders = [];
                    state.parameters.risks = [];
                    state.parameters.riskLevels = [];

                    state.modeling.rolesForView = [];
                    modelingRolesAdapter.removeAll(state.modeling.roles);
                }
            })
            .addCase(deleteVariant.rejected, (state, action) => {
                state.busy = false;
                if (!action.payload) return;
                state.error = action.payload;
            })
            .addCase(setFavoritesForVariant.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.SAVE;
            })
            .addCase(setFavoritesForVariant.fulfilled, (state, action) => {
                const { variantName, inFavorites } = action.payload;

                state.busy = false;

                const variant = state.variants.find((variant) => variant.name === variantName);

                if (variant) {
                    variant.inFavorites = inFavorites;
                }

                if (state.currentVariant?.id === variant?.id && state.currentVariant) {
                    state.currentVariant.inFavorites = inFavorites;
                }
            })
            .addCase(setFavoritesForVariant.rejected, (state, action) => {
                state.busy = false;
                if (!action.payload) return;
                state.error = action.payload;
            })
            .addCase(fetchVariantsByUser.fulfilled, (state, action) => {
                state.variants = action.payload;
            })
            .addCase(fetchVariantsByUser.rejected, (state, action) => {
                if (!action.payload) return;
                state.error = action.payload;
            })
            .addCase(exportReport.rejected, (state, action) => {
                if (!action.payload) return;
                state.error = action.payload;
            })
            .addCase(saveReportColumns.fulfilled, (state, action) => {
                const { columns, reportLevel, reportType, conflictLevel } = action.payload;

                if (
                    state.report.reportLevel === reportLevel &&
                    state.report.reportType === reportType &&
                    conflictLevel === state.report.conflictLevel
                ) {
                    state.report.columns = columns.filter((column) => column.active);
                }
            })
            .addCase(setSelectedViewRole.fulfilled, (state, action) => {
                state.modeling.selectedViewRole = action.payload;
            })
            .addCase(setModelingSelectedRole.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })
            .addCase(setModelingSelectedRole.fulfilled, (state, action) => {
                const role = action.payload;

                if (!role) {
                    state.modeling.selectedRoleId = "";
                } else if (role.id in state.modeling.roles.entities) {
                    state.modeling.selectedRoleId = role.id;
                    modelingRolesAdapter.setOne(state.modeling.roles, role);
                }

                state.busy = false;
            })
            .addCase(setModelingSelectedRole.rejected, (state, action) => {
                state.busy = false;
                if (!action.payload) return;
                state.error = action.payload;
            })
            .addCase(initModelingByVariant.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })
            .addCase(initModelingByVariant.fulfilled, (state, action: PayloadAction<{ variant: RoleVariantDetailed }>) => {
                const { variant } = action.payload;

                const rolesForModeling: ModelingRole[] = [];

                variant.roles.forEach((role) => {
                    const modelingRole = createRoleForModeling(role);

                    rolesForModeling.push(modelingRole);

                    role.children.forEach((childRole) => {
                        const childRoleForView = createRoleForViewByChildren(childRole);

                        const modelingChildRole = createRoleForModeling(childRoleForView);

                        rolesForModeling.push(modelingChildRole);
                    });
                });

                modelingRolesAdapter.setAll(state.modeling.roles, rolesForModeling);

                state.modeling.rolesForView = variant.roles;
                state.modeling.selectedViewRole = variant.roles[0];
                state.modeling.selectedRoleId = variant.roles[0].id;

                state.currentVariant = variant;

                state.busy = false;
            })
            .addCase(initModelingByVariant.rejected, (state, action) => {
                state.busy = false;
                if (!action.payload) return;
                state.error = action.payload;
            })
            .addCase(initModelingManual.pending, (state) => {
                state.busy = true;
                state.busyType = BUSY_TYPES.LOAD;
            })
            .addCase(initModelingManual.fulfilled, (state, action: PayloadAction<{ roleForView: RoleForView; modelingRole: ModelingRole }>) => {
                const { roleForView, modelingRole } = action.payload;

                modelingRolesAdapter.addOne(state.modeling.roles, modelingRole);

                roleForView.children.forEach((child) => {
                    const childRoleForView = createRoleForViewByChildren(child);

                    const modelingChildRole = createRoleForModeling(childRoleForView);

                    modelingRolesAdapter.addOne(state.modeling.roles, modelingChildRole);
                });

                state.modeling.rolesForView = [roleForView];

                state.busy = false;
                state.error = null;
            })
            .addCase(initModelingManual.rejected, (state, action) => {
                state.busy = false;
                if (!action.payload) return;
                state.error = action.payload;
            });
    },
});

export const {
    setRoles,
    setBusy,
    setSystems,
    setMatrixHeaders,
    setRisks,
    setRiskLevels,
    setParametersOpen,
    setResultsOpen,
    setReportLevel,
    setReportType,
    setError,
    setSelectedIds,
    setSearchString,
    setPageNumber,
    setLimitNumber,
    setSortData,
    setFilterData,
    setCurrentVariantFields,
    clearFilters,
    setModelingOpen,
    addModelingRoleTreeItems,
    setModelingRoleOrgLevelValues,
    setModelingRoleTreeField,
    openRoleMenuNode,
    addRoleMenuFolder,
    addRoleMenuNode,
    removeRoleMenuNode,
    copyRoleMenu,
    addGroupRoleChild,
    removeGroupRoleChild,
    removeModelingRoleTreeItem,
    setConflictLevel,
    substractPfcgTreeFromRole,
    clearVariant,
    expandItemTree,
    setRolesForView,
    addModelingRole,
    expandGroupRole,
    removeRoleFromModeling,
    updateModelingRole,
    setOpenModeling,
    clearReport,
    clearParameters
    // findPermissionInTree
} = roleLevelSlice.actions;

export const {
    selectIds: selectModelingRoleIds,
    selectAll: selectModelingRoles,
    selectById: selectModelingRoleById,
} = modelingRolesAdapter.getSelectors((state: RootState) => state.reportsModeling.roleLevel.modeling.roles);

export const selectModelingRolesForViewBySearch = (state, searchString = "") => {
    const roles = state.reportsModeling.roleLevel.modeling.rolesForView;
    const search = searchString.toLowerCase();
    return roles.filter(({ role }) => role.toLowerCase().includes(search));
};

export const selectModelingRolesBySearch = createSelector(
    selectModelingRoles,
    (_: RootState, searchString: string = "") => searchString.toLowerCase(),
    (roles: ModelingRole[], searchString: string) => roles.filter(({ role }) => role.toLowerCase().includes(searchString))
);

export const selectSelectedViewRole = (state) => state.reportsModeling.roleLevel.modeling.selectedViewRole;
export const selectModelingSelectedRoleId = (state) => state.reportsModeling.roleLevel.modeling.selectedRoleId;

export const selectModelingSelectedRole = (state) => {
    const selectedRoleId = selectModelingSelectedRoleId(state);
    if (!selectedRoleId) return null;
    return selectModelingRoleById(state, selectedRoleId);
};

export const selectModelingRolesContainsGroupRoles = createSelector(selectModelingRoles, (roles) => {
    for (let i = 0; i < roles.length; i++) {
        if (!roles[i].isComplex) return false;
    }
    return true;
});

export const selectModelingSystemId = (state: RootState) => {
    const systems = state.reportsModeling.roleLevel.parameters.systems;

    return (systems && systems[0] && systems[0].key) || null;
};

export const selectModelingOpen = (state) => state.reportsModeling.roleLevel.modeling.open;

export const selectCurrentVariantName = (state) => state.reportsModeling.roleLevel.currentVariant?.variantName;

export default roleLevelSlice.reducer;

// async function getPrefetchedRolesForVariant(roles, currentVariantName) {
//     const rolesToFetch = roles.filter((role) => !role.isComplex && !role.initialized);

//     const prefetchedRoleMap = await getRolePermissionsMap(rolesToFetch, currentVariantName, PERMISSIONS_TREE_VARIANT_LEVEL.ROLES, "");

//     return prefetchedRoleMap;
// }
